import { Grid, useMediaQuery, useTheme } from '@mui/material';
import UiBox from '../Box';
import MuiCarousel from 'react-material-ui-carousel';
import { useState } from 'react';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import useDetectSmallScreenUsingUserAgent from '../../../lib/utils/user-agent.utility';

type CarouselProp = {
  children: React.ReactNode[];
  columnSize: number;
  disable?: boolean;
  spacing?: number;
  showScrollbar?: boolean;
};

type CarouselLayout = {
  itemCount: number;
  children: React.ReactNode[];
  disableCarousel?: boolean;
  isSmallScreen: boolean;
  showScrollbar?: boolean;
  columnSize: number;
};

type CarouselItemProp = {
  children: React.ReactNode;
  fullWidth?: boolean;
  columnSize: number;
};

const DEFAULT_GUTTER_SPACE = 2;
const SPACING = '16px';
const DEFAULT_COL_SIZE = 3;
const ColSize = {
  1: 12,
  2: 6,
  3: 4,
  4: 3,
};
const ColSizes = [ColSize[DEFAULT_COL_SIZE], ColSize[1], ColSize[2], ColSize[3], ColSize[4]];

function LayoutContainer({
  disableCarousel,
  isSmallScreen,
  children,
  itemCount,
  showScrollbar,
  columnSize,
}: CarouselLayout) {
  const [activePage, setPage] = useState(0);
  const theme = useTheme();

  if (disableCarousel) {
    itemCount = 1;
  }
  if (disableCarousel || isSmallScreen) {
    return (
      <UiBox
        styles={{
          margin: '0 -' + SPACING,
          padding: SPACING,
          overflow: 'auto',
          scrollbarWidth: showScrollbar ? 'auto' : 'none',
          maxWidth: 'calc(100vw - 10px)',
        }}
      >
        <UiBox
          styles={{
            display: 'grid',
            gridTemplateColumns: `repeat(${itemCount}, 1fr)`,
            gap: SPACING,
            alignItems: 'stretch',
          }}
        >
          {children}
        </UiBox>
      </UiBox>
    );
  }

  const disabledBtnStyle = {
    pointerEvents: 'none',
    opacity: 0.7,
    color: '#979797 !important',
  };
  const showNavigator = itemCount > columnSize;

  return (
    <MuiCarousel
      autoPlay={false}
      navButtonsAlwaysVisible={showNavigator}
      indicators={showNavigator}
      animation="slide"
      navButtonsProps={{
        style: {
          background: 'none',
          color: theme.palette.primary.main,
          margin: 0,
        },
      }}
      activeIndicatorIconButtonProps={{
        style: {
          color: theme.palette.primary.main,
        },
      }}
      onChange={(now) => setPage(now ?? 0)}
      sx={{
        'button[aria-label="Previous"]':
          showNavigator && activePage === 0 ? disabledBtnStyle : null,
        'button[aria-label="Next"]':
          showNavigator && activePage >= children?.length - 1 ? disabledBtnStyle : null,
      }}
      PrevIcon={showNavigator && <ArrowBackIosIcon sx={{ width: 40, height: 40 }} />}
      NextIcon={showNavigator && <ArrowForwardIosIcon sx={{ width: 40, height: 40 }} />}
    >
      {children}
    </MuiCarousel>
  );
}

function Carousel({
  columnSize,
  disable,
  spacing = DEFAULT_GUTTER_SPACE,
  showScrollbar,
  children,
}: CarouselProp) {
  const theme = useTheme();
  const isNonDesktop = useDetectSmallScreenUsingUserAgent();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const groupedCards = groupItems<React.ReactNode>(
    children,
    isNonDesktop || isMobile ? 1 : columnSize
  );

  return (
    <LayoutContainer
      isSmallScreen={isMobile}
      disableCarousel={disable}
      itemCount={children.length}
      showScrollbar={showScrollbar}
      columnSize={columnSize}
    >
      {groupedCards.map((nodeGroup, key) => (
        <UiBox
          className="ui-card-list-group"
          key={key}
          styles={{
            margin: '0 auto',
            width: !isMobile ? '90%' : '100%',
            transform: 'translateZ(0)',
          }}
        >
          <Grid
            columnSpacing={spacing}
            container
            justifyContent="center"
            alignItems="stretch"
            sx={{ height: '100%' }}
          >
            {nodeGroup}
          </Grid>
        </UiBox>
      ))}
    </LayoutContainer>
  );
}

function groupItems<T>(items: T[], groupSize?: number | null) {
  return items.reduce<T[][]>((acc, item, index) => {
    const groupIndex = groupSize ? Math.floor(index / groupSize) : 0;
    if (!acc[groupIndex]) acc[groupIndex] = [];
    acc[groupIndex].push(item);
    return acc;
  }, []);
}

function CarouselItem({ children, columnSize, fullWidth }: CarouselItemProp) {
  const theme = useTheme();
  const isNonDesktop = useDetectSmallScreenUsingUserAgent();
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const minWidth = isMobile ? 300 : isNonDesktop || isTablet ? 396 : 'auto';

  return (
    <Grid
      item
      xs={12}
      md={fullWidth ? 12 : 6}
      lg={fullWidth ? 12 : ColSizes[columnSize]}
      sx={{ minWidth: minWidth }}
    >
      {children}
    </Grid>
  );
}

Carousel.Item = CarouselItem;

export default Carousel;
